import { Spacing8 } from 'autogen/design-tokens/tokens';
import { GetVariantColorPickerForPdp, type VariantPickerListItem } from 'autogen/swagger/all';
import {
    ColorPrefix,
    MiscellaneousPrefix,
    SizePrefix
} from 'autogen/translation-keys/trans-website-product-productvariants-variantsection';
import {
    ChosenColorPrefix,
    ChosenMiscellaneousPrefix,
    ChosenSizePrefix
} from 'autogen/translation-keys/trans-website-productvariantpicker';
import { Typography } from 'components/generic/typography';
import type { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import useVariantPickerQuickView from '../VariantPicker/shared/hooks/useVariantPickerQuickView';
import InlineButton from 'components/generic/InlineButton';
import { GA4Tracking } from 'modules/tracking/GA4';
import { SeeAll } from 'autogen/translation-keys/trans-website-product-productvariants-variantpickerbutton';

// #region Styles
const Headline = styled(Typography.Body)`
    display: flex;
    justify-content: space-between;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Name = styled.span`
    font-weight: bold;
    margin-left: ${Spacing8};
`;

function useGetPrefixByCategory(category: VariantPickerListItem['category'], isVariantPicker: boolean) {
    const { t } = useTranslation();

    switch (category) {
        case 'Color':
            return isVariantPicker ? t(ChosenColorPrefix) : t(ColorPrefix);
        case 'Size':
            return isVariantPicker ? t(ChosenSizePrefix) : t(SizePrefix);
        case 'Miscellaneous':
        default:
            return isVariantPicker ? t(ChosenMiscellaneousPrefix) : t(MiscellaneousPrefix);
    }
}

type SectionHeaderProps = VariantPickerListItem &
    HTMLAttributes<HTMLDivElement> & {
        isVariantPicker?: boolean;
        variantPickerButtonText?: string;
    };

export default function SectionHeader({
    category,
    name,
    isVariantPicker = false,
    productId,
    variantPickerButtonText,
    ...restProps
}: SectionHeaderProps) {
    const prefix = useGetPrefixByCategory(category, isVariantPicker);
    const [, toggle] = useVariantPickerQuickView();
    const { t } = useTranslation();

    return (
        <Headline {...restProps} forwardedAs="div">
            <div>
                {prefix}
                <Name>{name}</Name>
            </div>
            {!isVariantPicker && !!variantPickerButtonText && (
                <InlineButton
                    onClick={async () => {
                        await GetVariantColorPickerForPdp({ id: productId }).then((res) => {
                            toggle({
                                isOpen: true,
                                variantPickerModel: res,
                                isPDPRequest: true
                            });

                            GA4Tracking({
                                eventName: 'open_variant_picker',
                                context: 'product_page',
                                category: 'ecommerce'
                            });
                        });
                    }}
                >
                    {t(SeeAll)}
                </InlineButton>
            )}
        </Headline>
    );
}
